import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// All dashboard CRUD handlers. Kept as a plain factory (no internal React state);
// UI state (modals, confirm, suggestion) is owned by Dashboard and passed in.
export function useDashboardActions({
  facility,
  orderModal,
  suggestion,
  loadCore,
  loadUsers,
  reloadFacilities,
  setOrders,
  setSuggestion,
  setConfirm,
}) {
  const notifyCapacity = (res) => {
    if (res?.capacity_warning) {
      const w = res.capacity_warning;
      toast.warning(`${w.line_code} overloaded: ${w.load.toLocaleString("en-IN")} units vs ${w.capacity.toLocaleString("en-IN")} daily capacity (${w.utilization}%)`, { duration: 5000 });
    }
  };

  const saveOrder = async (payload) => {
    try {
      let res;
      if (orderModal.order) {
        res = await api.put(`/orders/${orderModal.order.id}`, payload);
        toast.success("Order updated");
      } else {
        res = await api.post("/orders", { ...payload, tenant_id: facility });
        toast.success("Order created");
        if (res?.generated_work_orders?.length) {
          const parts = res.generated_work_orders.map((w) => `${w.order_code} (${w.action === "updated" ? `+${w.added}` : w.added} pcs)`).join(", ");
          toast.info(`Work orders synced for spare parts: ${parts}`, { duration: 6000 });
        }
      }
      notifyCapacity(res);
      loadCore();
      return res;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const rescheduleOrder = async (orderId, patch) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o))); // optimistic
    try {
      const res = await api.put(`/orders/${orderId}`, patch);
      toast.success("Order rescheduled");
      notifyCapacity(res);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      loadCore();
    }
  };

  const suggestFix = async (o) => {
    setSuggestion({ order: o, data: null, loading: true });
    try {
      const data = await api.get(`/orders/${o.id}/suggestion`);
      setSuggestion({ order: o, data, loading: false });
    } catch (e) {
      setSuggestion(null);
      toast.error(formatApiErrorDetail(e.detail));
    }
  };

  const applySuggestion = async () => {
    const { order, data } = suggestion;
    setSuggestion(null);
    try {
      const res = await api.put(`/orders/${order.id}`, { line_id: data.line_id, start_date: data.start_date });
      toast.success(`${order.order_code} rescheduled — conflict resolved`);
      notifyCapacity(res);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };

  const deleteOrder = (o) =>
    setConfirm({
      title: `Delete ${o.order_code}?`,
      desc: `This permanently removes "${o.product_name}".`,
      action: async () => {
        try {
          await api.del(`/orders/${o.id}`);
          toast.success("Order deleted");
          loadCore();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.detail));
        }
      },
    });

  const gpToast = (order, res, verb) => {
    const gpId = res.gate_pass?.id;
    const gpNo = res.gate_pass?.gp_number || "gate pass";
    toast.success(`${order.order_code} ${verb} · ${gpNo}`, {
      duration: 10000,
      action: gpId ? { label: "Print Gate Pass", onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/osp/gate-passes/${gpId}/pdf`, "_blank") } : undefined,
    });
  };
  const stageName = (res, idx) => res?.stages?.[idx]?.name || "stage";
  const logStage = async (order, idx, quantity) => {
    try {
      const res = await api.post(`/orders/${order.id}/stages/${idx}/log`, { quantity });
      if (res.status === "Completed") toast.success(`${order.order_code} completed — all ${(res.target_quantity || 0).toLocaleString("en-IN")} pcs through ${stageName(res, idx)}`);
      else toast.success(`${order.order_code}: ${quantity.toLocaleString("en-IN")} pcs logged at ${stageName(res, idx)}`);
      loadCore();
      return res;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const shipStage = async (order, idx, payload) => {
    try {
      const res = await api.post(`/orders/${order.id}/stages/${idx}/ship`, payload);
      gpToast(order, res, `— ${Number(payload.quantity).toLocaleString("en-IN")} pcs sent to ${res.gate_pass?.vendor_name || "vendor"}`);
      loadCore();
      return res;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const receiveStage = async (order, idx, quantity) => {
    try {
      const res = await api.post(`/orders/${order.id}/stages/${idx}/receive`, { quantity });
      const o = res.order || {};
      if (o.status === "Completed") toast.success(`${order.order_code} completed — final stage received in full`);
      else toast.success(`${order.order_code}: ${quantity.toLocaleString("en-IN")} pcs received back — available for the next stage`);
      loadCore();
      return res;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const receiveGatePass = async (gpId, quantity) => {
    if (!gpId) { toast.error("No open gate pass to receive"); return; }
    try {
      const res = await api.post(`/osp/gate-passes/${gpId}/receive`, quantity ? { quantity } : {});
      const gp = res.gate_pass || {};
      toast.success(gp.status === "Received" ? `${gp.gp_number} fully received — back in the line` : `${gp.gp_number}: partial receipt · ${(gp.pending_qty || 0).toLocaleString("en-IN")} still pending`);
      loadCore();
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const createOspVendor = async (payload) => {
    try { await api.post(`/osp/vendors`, payload); toast.success("OSP vendor added"); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };

  const createWorkOrder = async (payload) => {
    try {
      const r = await api.post("/orders", { ...payload, kind: "work_order", tenant_id: facility });
      toast.success(`Work order ${r?.order_code || ""} created`.trim());
      loadCore();
      return r;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const editWorkOrder = async (orderId, payload) => {
    try {
      const res = await api.put(`/orders/${orderId}`, payload);
      toast.success("Work order updated — schedule & linked sales order synced");
      notifyCapacity(res);
      loadCore();
      return res;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const deleteWorkOrder = (wo) =>
    setConfirm({
      title: `Delete ${wo.order_code}?`,
      desc: `This permanently removes the work order for "${wo.product_name}".`,
      action: async () => {
        try { await api.del(`/orders/${wo.id}`); toast.success("Work order deleted"); loadCore(); }
        catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
      },
    });

  const createMachine = async (payload) => {
    try {
      await api.post("/machines", { ...payload, tenant_id: facility });
      toast.success("Line added");
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };
  const updateMachine = async (m, patch) => {
    try {
      await api.put(`/machines/${m.id}`, patch);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };
  const deleteMachine = (m) =>
    setConfirm({
      title: `Delete ${m.line_code}?`,
      desc: `Orders on this line will be unassigned.`,
      action: async () => {
        try {
          await api.del(`/machines/${m.id}`);
          toast.success("Line deleted");
          loadCore();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.detail));
        }
      },
    });

  const logDowntime = async (payload) => {
    try {
      const res = await api.post("/downtime", payload);
      if (res.affected_orders > 0) {
        toast.warning(`Downtime logged — ${res.affected_orders} order${res.affected_orders > 1 ? "s" : ""} flagged with capacity conflicts`);
      } else {
        toast.success("Downtime logged — no orders affected");
      }
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };
  const deleteDowntime = (d) =>
    setConfirm({
      title: `Remove downtime on ${d.line_code}?`,
      desc: `"${d.reason}" — conflict flags on affected orders will clear.`,
      action: async () => {
        try {
          await api.del(`/downtime/${d.id}`);
          toast.success("Downtime log removed");
          loadCore();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.detail));
        }
      },
    });

  const createUser = async (payload) => {
    try {
      await api.post("/users", payload);
      toast.success("User created");
      loadUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };
  const updateUser = async (u, patch) => {
    try {
      await api.put(`/users/${u.id}`, patch);
      toast.success("User updated");
      loadUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };
  const deleteUser = (u) =>
    setConfirm({
      title: `Delete ${u.name}?`,
      desc: `${u.username} will lose access immediately.`,
      action: async () => {
        try {
          await api.del(`/users/${u.id}`);
          toast.success("User deleted");
          loadUsers();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.detail));
        }
      },
    });

  const savePlantSettings = async (payload) => {
    try {
      await api.put(`/facilities/${facility}`, payload);
      toast.success("Plant settings saved — schedules re-estimated");
      reloadFacilities();
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const saveBomItem = async (payload, existing) => {
    try {
      if (existing) {
        await api.put(`/bom/items/${existing.id}`, payload);
        toast.success("BOM item updated");
      } else {
        await api.post("/bom/items", { ...payload, tenant_id: facility });
        toast.success("BOM item created");
      }
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };
  const adjustStock = async (item, payload) => {
    try {
      const res = await api.post(`/bom/items/${item.id}/stock`, payload);
      toast.success(`${item.sku} stock updated → ${res.stock_quantity.toLocaleString("en-IN")} ${item.unit}`);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };
  const deleteBomItem = (item) =>
    setConfirm({
      title: `Delete ${item.sku}?`,
      desc: `${item.name} will be permanently removed. If it's linked to active orders this will be blocked — Archive it instead to keep history.`,
      action: async () => {
        try {
          await api.del(`/bom/items/${item.id}`);
          toast.success("BOM item deleted");
          loadCore();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.detail));
        }
      },
    });

  const archiveBomItem = async (item, archived) => {
    try {
      await api.post(`/bom/items/${item.id}/archive`, { archived });
      toast.success(archived ? `${item.sku} archived — hidden from new dropdowns` : `${item.sku} restored`);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };

  const createPO = async (s, supplierOverride) => {
    try {
      await api.post("/purchase-orders", {
        supplier: supplierOverride || s.supplier || "",
        lead_time_days: s.lead_time_days || 0,
        tenant_id: facility,
        items: [{ item_id: s.item_id, sku: s.sku, name: s.name, unit: s.unit || "pcs", ordered_qty: s.suggested_qty, unit_price: Number(s.unit_price) || 0 }],
      });
      toast.success(`Purchase order raised for ${s.sku} → ${supplierOverride || s.supplier || "supplier"}`);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const createBulkPO = async (supplier, items, leadTime = 0) => {
    try {
      await api.post("/purchase-orders", {
        supplier: supplier || "UNSPECIFIED",
        lead_time_days: leadTime,
        tenant_id: facility,
        items,
      });
      toast.success(`PO raised for ${supplier || "supplier"} (${items.length} item${items.length > 1 ? "s" : ""})`);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const reorderPO = async (poId, items) => {
    try {
      const res = await api.post(`/purchase-orders/${poId}/reorder`, items ? { items } : {});
      toast.success(`Reorder raised — ${res.po_number} (${res.supplier || "supplier"})`);
      loadCore();
      return res;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const orderAllShort = async () => {
    try {
      const res = await api.post(`/purchase-orders/order-all-short?facility=${encodeURIComponent(facility)}`, {});
      if (res.po_count > 0) {
        toast.success(`Raised ${res.po_count} PO${res.po_count > 1 ? "s" : ""} for materials short across all products`);
        if (res.skipped?.length) toast.info(`${res.skipped.length} material(s) skipped — already on an open PO`);
      } else if (res.skipped?.length) {
        toast.info(`Everything short is already on an open PO (${res.skipped.length} material${res.skipped.length > 1 ? "s" : ""})`);
      } else {
        toast.info("Nothing to order — no material shortfalls across products right now");
      }
      loadCore();
      return res;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const receivePO = async (po, mode, items) => {
    try {
      const res = await api.post(`/purchase-orders/${po.id}/receive`, { mode, items });
      toast.success(res.status === "Received" ? `${po.po_number} fully received — stock updated` : `${po.po_number}: partial delivery recorded`);
      loadCore();
      return res;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };

  const editPO = async (po, items) => {
    try { await api.put(`/purchase-orders/${po.id}`, { items }); toast.success(`${po.po_number} updated`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const cancelPO = async (po) => {
    try { await api.post(`/purchase-orders/${po.id}/cancel`); toast.success(`${po.po_number} cancelled`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const approvePO = async (po, note) => {
    try { await api.post(`/purchase-orders/${po.id}/approve`, { note: note || null }); toast.success(`${po.po_number} approved — now active`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const rejectPO = async (po, note) => {
    try { await api.post(`/purchase-orders/${po.id}/reject`, { note: note || null }); toast.success(`${po.po_number} rejected — sent back to planner`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const resubmitPO = async (po, items) => {
    try { const r = await api.post(`/purchase-orders/${po.id}/resubmit`, { items }); toast.success(`Resubmitted as ${r.po_number}`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const bulkApprove = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      const res = await api.post(`/purchase-orders/approve-bulk`, { ids });
      toast.success(`${res.count} purchase order(s) approved`);
      loadCore();
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const updateSupplier = async (s, patch) => {    try {
      const r = await api.put(`/suppliers/${s.id}`, patch);
      toast.success(r?.status === "Pending Approval" ? `${s.name} updated — sent back for admin approval` : "Supplier updated");
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
    }
  };
  const createSupplier = async (payload) => {
    try {
      const r = await api.post(`/suppliers`, payload);
      toast.success(r?.status === "Pending Approval" ? `Supplier "${payload.name}" sent for admin approval` : `Supplier "${payload.name}" added`);
      loadCore();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.detail));
      throw e;
    }
  };
  const createCustomer = async (payload) => {
    try {
      const r = await api.post(`/customers`, payload);
      toast.success(r?.status === "Pending Approval" ? `Customer "${payload.name}" sent for admin approval` : `Customer "${payload.name}" added`);
      loadCore();
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const updateCustomer = async (c, patch) => {
    try { const r = await api.put(`/customers/${c.id}`, patch); toast.success(r?.status === "Pending Approval" ? `${c.name} updated — sent back for admin approval` : "Customer updated"); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };

  // Supplier/Customer approval cycle: admin approves/rejects, planner resubmits
  const reviewMaster = (entity) => ({
    approve: async (rec) => {
      try { await api.post(`/${entity}/${rec.id}/approve`, {}); toast.success(`${rec.name} approved — now Active`); loadCore(); }
      catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
    },
    reject: async (rec, reason) => {
      try { await api.post(`/${entity}/${rec.id}/reject`, { reason }); toast.success(`${rec.name} rejected — sent back to planner`); loadCore(); }
      catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
    },
    resubmit: async (rec, payload) => {
      try { await api.post(`/${entity}/${rec.id}/resubmit`, payload); toast.success(`${rec.name} resubmitted for approval`); loadCore(); }
      catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
    },
  });
  const { approve: approveSupplier, reject: rejectSupplier, resubmit: resubmitSupplier } = reviewMaster("suppliers");
  const { approve: approveCustomer, reject: rejectCustomer, resubmit: resubmitCustomer } = reviewMaster("customers");
  const { approve: approveBomItem, reject: rejectBomItem } = reviewMaster("bom/items");

  const createSalesOrder = async (payload) => {
    try {
      const r = await api.post(`/sales-orders`, { ...payload, tenant_id: facility });
      toast.success(`Sales order ${r?.so_number || ""} created`.trim());
      const preview = r?.material_shortage_preview;
      if (preview?.has_shortage) {
        const skus = preview.lines.flatMap((l) => l.shortages.map((s) => s.sku)).slice(0, 4);
        toast.warning(`Material shortage for production: ${skus.join(", ")}${skus.length >= 4 ? "…" : ""} — raise POs before starting`, { duration: 8000 });
      }
      loadCore();
      return r;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const confirmSalesOrder = async (so, force = false) => {
    try {
      await api.post(`/sales-orders/${so.id}/confirm${force ? "?force=true" : ""}`);
      toast.success(`${so.so_number} confirmed`);
      loadCore();
      return { ok: true };
    } catch (e) {
      const d = e.detail;
      if (e.status === 409 && d && typeof d === "object" && Array.isArray(d.reservation_conflicts)) {
        return { conflict: d };  // let the Sales panel surface a "reserve anyway" warning
      }
      toast.error(formatApiErrorDetail(d));
      return { ok: false };
    }
  };
  const raisePosForSalesOrder = async (so) => {
    try {
      const res = await api.post(`/sales-orders/${so.id}/raise-pos`, {});
      if (res.po_count > 0) {
        toast.success(`Raised ${res.po_count} PO${res.po_count > 1 ? "s" : ""} to cover ${so.so_number}`);
        if (res.skipped?.length) toast.info(`${res.skipped.length} material(s) already on an open PO — skipped`);
      } else if (res.skipped?.length) {
        toast.info(`All short materials for ${so.so_number} are already on open POs`);
      } else {
        toast.info(res.message || "No material shortages to order");
      }
      loadCore();
      return res;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const createWorkOrderForSalesOrder = async (so, payload) => {
    try {
      const res = await api.post(`/sales-orders/${so.id}/work-order`, payload);
      toast.success(`Work order ${res?.work_order?.order_code || ""} created for ${so.so_number}`.trim());
      loadCore();
      return res;
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const fulfillSalesOrder = async (so) => {
    try { await api.post(`/sales-orders/${so.id}/fulfill`); toast.success(`${so.so_number} fulfilled — finished-goods stock shipped`); loadCore(); }
    catch (e) {
      const d = e.detail;
      if (d && typeof d === "object" && d.shortages) {
        toast.error(`${d.message}: ${d.shortages.map((s) => `${s.sku} short ${s.missing}`).join(", ")}`, { duration: 6000 });
      } else { toast.error(formatApiErrorDetail(d)); }
    }
  };
  const cancelSalesOrder = async (so) => {
    const n = (so.production_order_ids || []).length;
    let cascade = true;
    if (n > 0) {
      cascade = window.confirm(`Cancel ${so.so_number}?\n\nThis sales order has ${n} linked production order(s).\n\nOK = also cancel them\nCancel = keep the production order(s) running`);
    }
    try { const r = await api.post(`/sales-orders/${so.id}/cancel?cascade=${cascade}`); toast.success(`${so.so_number} cancelled${r?.cancelled_production_orders ? ` · ${r.cancelled_production_orders} production order(s) cancelled` : ""}`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); }
  };
  const updateSalesOrder = async (so, payload) => {
    try { await api.put(`/sales-orders/${so.id}`, payload); toast.success(`${so.so_number} updated`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const produceToStock = async (item, qty) => {
    try { await api.post(`/bom/items/${item.id}/produce`, { quantity: qty, facility }); toast.success(`Produced ${qty} ${item.unit || "units"} of ${item.name} — stock updated`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const applyBomRefresh = async (order) => {
    try { await api.post(`/orders/${order.id}/apply-bom-refresh`, {}); toast.success(`New BOM applied to ${order.order_code} — operators notified`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const acknowledgeBom = async (order) => {
    try { await api.post(`/orders/${order.id}/acknowledge-bom`, {}); toast.success(`Acknowledged updated stages for ${order.order_code}`); loadCore(); }
    catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const clearAllData = async (sections) => {
    try {
      const res = await api.post(`/admin/clear-data`, sections && sections.length ? { sections } : {});
      toast.success(res?.message || "Data cleared — user accounts preserved");
      loadCore();
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };
  const seedDemoData = async () => {
    try {
      const res = await api.post(`/admin/seed-demo`);
      toast.success(res?.message || "Demo dataset loaded");
      loadCore();
    } catch (e) { toast.error(formatApiErrorDetail(e.detail)); throw e; }
  };

  return {
    saveOrder, rescheduleOrder, suggestFix, applySuggestion, deleteOrder, editWorkOrder,
    createMachine, updateMachine, deleteMachine, logDowntime, deleteDowntime,
    createUser, updateUser, deleteUser, savePlantSettings,
    saveBomItem, adjustStock, deleteBomItem, archiveBomItem, approveBomItem, rejectBomItem,
    createPO, createBulkPO, reorderPO, orderAllShort, receivePO, editPO, cancelPO, approvePO, rejectPO, resubmitPO, bulkApprove, updateSupplier, createSupplier, createCustomer, updateCustomer,
    approveSupplier, rejectSupplier, resubmitSupplier, approveCustomer, rejectCustomer, resubmitCustomer,
    createSalesOrder, confirmSalesOrder, fulfillSalesOrder, cancelSalesOrder, updateSalesOrder, produceToStock, raisePosForSalesOrder, createWorkOrderForSalesOrder,
    logStage, shipStage, receiveStage, receiveGatePass, createOspVendor, createWorkOrder, deleteWorkOrder, applyBomRefresh, acknowledgeBom, clearAllData, seedDemoData,
  };
}
